import React, { useEffect, useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Bot, Landmark, MessageCircle, ArrowDown, AudioLines } from "lucide-react";
import { cn } from "@/lib/utils";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const INJECTED_STYLES = `
  .gsap-reveal { visibility: hidden; }

  .bg-grid-theme {
    background-size: 60px 60px;
    background-image:
      linear-gradient(to right, hsl(var(--foreground) / 0.05) 1px, transparent 1px),
      linear-gradient(to bottom, hsl(var(--foreground) / 0.05) 1px, transparent 1px);
    mask-image: radial-gradient(ellipse at center, black 0%, transparent 70%);
    -webkit-mask-image: radial-gradient(ellipse at center, black 0%, transparent 70%);
  }

  .text-3d-matte {
    color: hsl(var(--foreground));
    text-shadow: 0 2px 4px hsl(var(--foreground) / 0.1);
  }

  .text-silver-matte {
    background: linear-gradient(180deg, hsl(var(--foreground)) 0%, hsl(var(--foreground) / 0.4) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .text-card-silver-matte {
    background: linear-gradient(180deg, #FFFFFF 0%, #A1A1AA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .premium-depth-card {
    background: linear-gradient(145deg, #16233f 0%, #0a0c10 100%);
    box-shadow:
      0 40px 100px -20px rgba(0, 0, 0, 0.9),
      inset 0 1px 2px rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.05);
    position: relative;
    will-change: transform;
    backface-visibility: hidden;
  }

  .card-sheen {
    position: absolute; inset: 0; border-radius: inherit; pointer-events: none; z-index: 50;
    background: radial-gradient(800px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(255,255,255,0.06) 0%, transparent 40%);
  }

  .iphone-bezel {
    background-color: #111;
    box-shadow:
      inset 0 0 0 2px #52525B,
      inset 0 0 0 7px #000,
      0 40px 80px -15px rgba(0,0,0,0.9);
    transform-style: preserve-3d;
  }

  .hardware-btn {
    background: linear-gradient(90deg, #404040 0%, #171717 100%);
    box-shadow:
      -2px 0 5px rgba(0,0,0,0.8),
      inset -1px 0 1px rgba(255,255,255,0.15),
      inset 1px 0 2px rgba(0,0,0,0.8);
    border-left: 1px solid rgba(255,255,255,0.05);
  }

  .screen-glare {
    background: linear-gradient(110deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 45%);
  }

  .chat-bubble-bot {
    background: #1f2c34;
    box-shadow: 0 1px 1px rgba(0,0,0,0.4);
  }
  .chat-bubble-user {
    background: #005c4b;
    box-shadow: 0 1px 1px rgba(0,0,0,0.4);
  }

  .floating-ui-badge {
    background: linear-gradient(160deg, rgba(34, 44, 62, 0.96) 0%, rgba(16, 20, 28, 0.96) 100%);
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.08),
      0 20px 40px -16px rgba(0, 0, 0, 0.8),
      inset 0 1px 0 rgba(255,255,255,0.1);
    will-change: transform, opacity;
  }

  .speech-badge {
    background: linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%);
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.08),
      0 20px 40px -16px rgba(0, 0, 0, 0.7),
      inset 0 1px 0 rgba(255,255,255,0.08);
  }
  .speech-bar {
    width: 2px;
    border-radius: 2px;
    background: linear-gradient(180deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.5) 100%);
    transform-origin: center;
    will-change: transform;
  }
  .speech-pulse {
    animation: speech-pulse 1.6s ease-in-out infinite;
  }
  @keyframes speech-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.35; transform: scale(0.7); }
  }
  .speech-transcript {
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    border-right: 1px solid hsl(var(--primary) / 0.8);
  }
  @media (prefers-reduced-motion: reduce) {
    .speech-pulse { animation: none; }
  }

  .btn-modern-light, .btn-modern-dark {
    transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.4s cubic-bezier(0.25, 1, 0.5, 1), background 0.4s;
  }
  .btn-modern-light {
    background: linear-gradient(180deg, #FFFFFF 0%, #F1F5F9 100%);
    color: #0F172A;
    box-shadow: 0 0 0 1px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.1), 0 12px 24px -4px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,1), inset 0 -3px 6px rgba(0,0,0,0.06);
  }
  .btn-modern-light:hover {
    transform: translateY(-3px);
    box-shadow: 0 0 0 1px rgba(0,0,0,0.05), 0 6px 12px -2px rgba(0,0,0,0.15), 0 20px 32px -6px rgba(0,0,0,0.4), inset 0 1px 1px rgba(255,255,255,1), inset 0 -3px 6px rgba(0,0,0,0.06);
  }
  .btn-modern-light:active {
    transform: translateY(1px);
    background: linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 100%);
    box-shadow: 0 0 0 1px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1), inset 0 3px 6px rgba(0,0,0,0.1);
  }
  .btn-modern-dark {
    background: linear-gradient(180deg, #27272A 0%, #18181B 100%);
    color: #FFFFFF;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.1), 0 2px 4px rgba(0,0,0,0.6), 0 12px 24px -4px rgba(0,0,0,0.9), inset 0 1px 1px rgba(255,255,255,0.15), inset 0 -3px 6px rgba(0,0,0,0.8);
  }
  .btn-modern-dark:hover {
    transform: translateY(-3px);
    background: linear-gradient(180deg, #3F3F46 0%, #27272A 100%);
    box-shadow: 0 0 0 1px rgba(255,255,255,0.15), 0 6px 12px -2px rgba(0,0,0,0.7), 0 20px 32px -6px rgba(0,0,0,1), inset 0 1px 1px rgba(255,255,255,0.2), inset 0 -3px 6px rgba(0,0,0,0.8);
  }
  .btn-modern-dark:active {
    transform: translateY(1px);
    background: #18181B;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.05), inset 0 3px 8px rgba(0,0,0,0.9);
  }
`;

export function CinematicHero({
  id,
  brandName,
  tagline1,
  tagline2,
  cardHeading,
  cardDescription,
  chat,
  badges = [],
  speech,
  ctaHeading,
  ctaDescription,
  primaryLabel,
  secondaryLabel,
  onPrimaryClick,
  secondaryHref,
  className,
  ...props
}) {
  const containerRef = useRef(null);
  const mainCardRef = useRef(null);
  const mockupRef = useRef(null);

  // Mouse parallax: quickTo reuses one tween per property instead of
  // spawning a new tween on every mousemove.
  useEffect(() => {
    const mockup = mockupRef.current;
    const card = mainCardRef.current;
    if (!mockup || !card) return;
    if (window.matchMedia?.("(pointer: coarse)")?.matches) return;

    const rotY = gsap.quickTo(mockup, "rotationY", { duration: 0.8, ease: "power3.out" });
    const rotX = gsap.quickTo(mockup, "rotationX", { duration: 0.8, ease: "power3.out" });
    let raf = 0;
    let lastEvent = null;

    const tick = () => {
      raf = 0;
      const e = lastEvent;
      if (!e) return;
      const rect = card.getBoundingClientRect();
      card.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
      card.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
      const xVal = (e.clientX / window.innerWidth - 0.5) * 2;
      const yVal = (e.clientY / window.innerHeight - 0.5) * 2;
      rotY(xVal * 10);
      rotX(-yVal * 10);
    };

    const handleMouseMove = (e) => {
      if (window.scrollY > window.innerHeight * 6) return;
      lastEvent = e;
      if (!raf) raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useLayoutEffect(() => {
    const isMobile = window.innerWidth < 768;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    const cardScale = isMobile ? 0.92 : 0.85;
    const cardRadius = isMobile ? 32 : 40;

    const ctx = gsap.context(() => {
      gsap.set(".text-track", { autoAlpha: 0, y: 60, scale: 0.9 });
      gsap.set(".text-days", { autoAlpha: 1, clipPath: "inset(0 100% 0 0)" });
      gsap.set(".main-card", {
        yPercent: 120,
        scale: cardScale,
        borderRadius: cardRadius,
        autoAlpha: 1,
        force3D: true,
      });
      gsap.set([".card-left-text", ".card-right-text", ".mockup-scroll-wrapper", ".floating-badge", ".phone-widget"], { autoAlpha: 0 });
      gsap.set(".speech-transcript", { width: 0 });

      // Waveform: continuous loop, independent of scroll.
      if (!reduced) {
        const bars = gsap.utils.toArray(".speech-bar");
        bars.forEach((bar, i) => {
          gsap.fromTo(
            bar,
            { scaleY: 0.25 },
            {
              scaleY: gsap.utils.random(0.7, 1),
              duration: gsap.utils.random(0.35, 0.7),
              ease: "sine.inOut",
              repeat: -1,
              yoyo: true,
              repeatRefresh: true,
              delay: i * 0.04,
            },
          );
        });
      } else {
        gsap.set(".speech-bar", { scaleY: 0.6 });
      }
      gsap.set(".cta-wrapper", { autoAlpha: 0, scale: 0.9 });

      gsap
        .timeline({ delay: 0.2 })
        .to(".text-track", { duration: reduced ? 0.01 : 1.4, autoAlpha: 1, y: 0, scale: 1, ease: "expo.out" })
        .to(".text-days", { duration: reduced ? 0.01 : 1.2, clipPath: "inset(0 0% 0 0)", ease: "power4.inOut" }, "-=0.9");

      const scrollTl = gsap.timeline({
        defaults: { force3D: true },
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "+=5500",
          pin: true,
          scrub: 0.8,
          anticipatePin: 1,
          // Refresh before sections further down so their offsets account
          // for this pin spacer.
          refreshPriority: 1,
          invalidateOnRefresh: true,
        },
      });

      scrollTl
        .to([".hero-text-wrapper", ".bg-grid-theme"], { scale: 1.12, autoAlpha: 0.15, ease: "power2.inOut", duration: 2 }, 0)
        .to(".main-card", { yPercent: 0, ease: "power3.inOut", duration: 2 }, 0)
        .to(".main-card", { scale: 1, borderRadius: 0, ease: "power3.inOut", duration: 1.5 })
        .fromTo(
          ".mockup-scroll-wrapper",
          { y: 220, z: -400, rotationX: 40, rotationY: -25, autoAlpha: 0, scale: 0.7 },
          { y: 0, z: 0, rotationX: 0, rotationY: 0, autoAlpha: 1, scale: 1, ease: "expo.out", duration: 2.5 },
          "-=0.8",
        )
        .fromTo(".phone-widget", { y: 30, autoAlpha: 0 }, { y: 0, autoAlpha: 1, stagger: 0.15, ease: "power3.out", duration: 1.2 }, "-=1.5")
        .fromTo(".floating-badge", { y: 80, autoAlpha: 0, scale: 0.8, rotationZ: -6 }, { y: 0, autoAlpha: 1, scale: 1, rotationZ: 0, ease: "back.out(1.4)", duration: 1.5, stagger: 0.2 }, "-=1.5")
        .to(".speech-transcript", { width: "auto", ease: "steps(28)", duration: 1.4 }, "-=0.6")
        .fromTo(".card-left-text", { x: -50, autoAlpha: 0 }, { x: 0, autoAlpha: 1, ease: "power4.out", duration: 1.5 }, "-=1.5")
        .fromTo(".card-right-text", { x: 50, autoAlpha: 0, scale: 0.85 }, { x: 0, autoAlpha: 1, scale: 1, ease: "expo.out", duration: 1.5 }, "<")
        .to({}, { duration: 2 })
        .set(".hero-text-wrapper", { autoAlpha: 0 })
        .set(".cta-wrapper", { autoAlpha: 1 })
        .to({}, { duration: 1 })
        .to([".mockup-scroll-wrapper", ".floating-badge", ".card-left-text", ".card-right-text"], {
          scale: 0.9, y: -40, z: -200, autoAlpha: 0, ease: "power3.in", duration: 1.2, stagger: 0.05,
        })
        .to(".main-card", { scale: cardScale, borderRadius: cardRadius, ease: "expo.inOut", duration: 1.8 })
        .to(".main-card", { yPercent: -130, ease: "power2.inOut", duration: 2 })
        .fromTo(".cta-wrapper", { y: 40, scale: 0.94 }, { y: 0, scale: 1, ease: "power3.out", duration: 1.6 }, "-=1.2")
        .to({}, { duration: 3 });
    }, containerRef);

    // Pin spacer changed document height; recalc every trigger on the page.
    ScrollTrigger.refresh();

    return () => {
      ctx.revert();
      ScrollTrigger.refresh();
    };
  }, []);

  const badgeIcons = [Bot, Landmark];

  return (
    <div
      id={id}
      ref={containerRef}
      className={cn(
        "relative w-full h-screen overflow-hidden flex items-center justify-center bg-background text-foreground antialiased",
        className,
      )}
      style={{ perspective: "1500px" }}
      {...props}
    >
      <style dangerouslySetInnerHTML={{ __html: INJECTED_STYLES }} />
      <div className="bg-grid-theme absolute inset-0 z-0 pointer-events-none opacity-50 will-change-transform" aria-hidden="true" />

      {/* Hero texts */}
      <div
        className="hero-text-wrapper absolute z-10 flex flex-col items-center justify-center text-center w-full px-4 will-change-transform"
        style={{ transformStyle: "preserve-3d" }}
      >
        <h1 className="text-track gsap-reveal text-3d-matte text-4xl md:text-6xl lg:text-[5.5rem] font-bold tracking-tight mb-2 leading-[1.05]">
          {tagline1}
        </h1>
        <h1 className="text-days gsap-reveal text-silver-matte text-4xl md:text-6xl lg:text-[5.5rem] font-extrabold tracking-tighter leading-[1.05] pb-2">
          {tagline2}
        </h1>
        <div className="mt-10 flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <ArrowDown size={14} />
        </div>
      </div>

      {/* CTA layer */}
      <div className="cta-wrapper absolute z-10 flex flex-col items-center justify-center text-center w-full px-4 gsap-reveal pointer-events-auto will-change-transform">
        <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight text-silver-matte pb-2">
          {ctaHeading}
        </h2>
        <p className="text-muted-foreground text-base md:text-lg mb-12 max-w-xl mx-auto leading-relaxed">
          {ctaDescription}
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={onPrimaryClick}
            className="btn-modern-light flex items-center justify-center gap-3 px-8 py-4 rounded-2xl text-base font-semibold focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
          >
            {primaryLabel}
          </button>
          <a
            href={secondaryHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-modern-dark flex items-center justify-center gap-3 px-8 py-4 rounded-2xl text-base font-semibold focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
          >
            <MessageCircle size={20} />
            {secondaryLabel}
          </a>
        </div>
      </div>

      {/* Deep card */}
      <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none" style={{ perspective: "1500px" }}>
        <div
          ref={mainCardRef}
          className="main-card premium-depth-card relative overflow-hidden gsap-reveal flex items-center justify-center pointer-events-auto w-full h-full"
        >
          <div className="card-sheen" aria-hidden="true" />

          <div className="relative w-full h-full max-w-7xl mx-auto px-4 lg:px-12 flex flex-col justify-evenly lg:grid lg:grid-cols-3 items-center lg:gap-6 z-10 py-6 lg:py-0">
            {/* Brand + speech panel */}
            <div className="card-right-text gsap-reveal order-1 lg:order-3 flex flex-col items-center lg:items-start z-20 w-full lg:pl-10">
              <h2 className="text-5xl md:text-7xl lg:text-[5rem] font-black uppercase tracking-tighter text-card-silver-matte leading-[0.9] text-center lg:text-left pb-2">
                {brandName}
              </h2>

              {speech && (
                <div className="speech-badge hidden lg:block mt-8 w-full max-w-[300px] rounded-2xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="relative w-9 h-9 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
                      <AudioLines size={16} className="text-primary" />
                      <span className="speech-pulse absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-semibold tracking-tight leading-tight">{speech.title}</p>
                      <p className="text-neutral-400 text-[11px] font-medium leading-tight">{speech.sub}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between h-7" aria-hidden="true">
                    {Array.from({ length: 32 }).map((_, i) => (
                      <span key={i} className="speech-bar h-full" />
                    ))}
                  </div>
                  <p className="mt-3 text-[11px] font-mono text-neutral-300 leading-none">
                    <span className="speech-transcript">{speech.transcript}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Phone mockup */}
            <div className="mockup-scroll-wrapper order-2 lg:order-2 relative w-full h-[380px] lg:h-[600px] flex items-center justify-center z-10 will-change-transform" style={{ perspective: "1000px" }}>
              <div className="relative w-full h-full flex items-center justify-center transform scale-[0.65] md:scale-[0.85] lg:scale-100">
                <div
                  ref={mockupRef}
                  className="relative w-[280px] h-[580px] rounded-[3rem] iphone-bezel flex flex-col will-change-transform"
                  style={{ transformStyle: "preserve-3d" }}
                >
                  <div className="absolute top-[120px] -left-[3px] w-[3px] h-[25px] hardware-btn rounded-l-md z-0" aria-hidden="true" />
                  <div className="absolute top-[160px] -left-[3px] w-[3px] h-[45px] hardware-btn rounded-l-md z-0" aria-hidden="true" />
                  <div className="absolute top-[220px] -left-[3px] w-[3px] h-[45px] hardware-btn rounded-l-md z-0" aria-hidden="true" />
                  <div className="absolute top-[170px] -right-[3px] w-[3px] h-[70px] hardware-btn rounded-r-md z-0 scale-x-[-1]" aria-hidden="true" />

                  <div className="absolute inset-[7px] bg-[#0b141a] rounded-[2.5rem] overflow-hidden shadow-[inset_0_0_15px_rgba(0,0,0,1)] text-white z-10">
                    <div className="absolute inset-0 screen-glare z-40 pointer-events-none" aria-hidden="true" />

                    <div className="absolute top-[5px] left-1/2 -translate-x-1/2 w-[100px] h-[28px] bg-black rounded-full z-50" />

                    {/* Chat UI */}
                    <div className="relative w-full h-full pt-12 pb-8 flex flex-col">
                      <div className="phone-widget flex items-center gap-3 px-4 pb-3 border-b border-white/5">
                        <div className="w-9 h-9 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center">
                          <Bot size={16} className="text-primary" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold leading-tight">{chat?.header}</span>
                          <span className="text-[10px] text-emerald-400">{chat?.status}</span>
                        </div>
                      </div>

                      <div className="flex-1 px-3 pt-4 space-y-2.5 overflow-hidden">
                        {chat?.messages?.map((m, i) => (
                          <div
                            key={i}
                            className={cn(
                              "phone-widget flex",
                              m.from === "user" ? "justify-end" : "justify-start",
                            )}
                          >
                            <div
                              className={cn(
                                "max-w-[82%] rounded-xl px-3 py-2 text-[11px] leading-snug",
                                m.from === "user" ? "chat-bubble-user rounded-tr-sm" : "chat-bubble-bot rounded-tl-sm",
                              )}
                            >
                              {m.text}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="phone-widget mx-3 mt-2 h-9 rounded-full bg-[#1f2c34] flex items-center px-4">
                        <span className="text-[11px] text-neutral-500">…</span>
                      </div>

                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[120px] h-[4px] bg-white/20 rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Floating badges */}
                {badges.slice(0, 2).map((b, i) => {
                  const Icon = badgeIcons[i] || Bot;
                  const pos =
                    i === 0
                      ? "top-6 lg:top-16 left-[-15px] lg:left-[-40px]"
                      : "bottom-8 lg:bottom-16 right-[-15px] lg:right-[-40px]";
                  return (
                    <div
                      key={i}
                      className={cn(
                        "floating-badge absolute flex floating-ui-badge rounded-xl lg:rounded-2xl p-3 lg:px-4 lg:py-3 items-center gap-3 z-30",
                        pos,
                      )}
                    >
                      <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-primary/15 flex items-center justify-center border border-primary/30">
                        <Icon size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-white text-xs lg:text-sm font-bold tracking-tight">{b.title}</p>
                        <p className="text-neutral-400 text-[10px] lg:text-xs font-medium">{b.sub}</p>
                      </div>
                    </div>
                  );
                })}

              </div>
            </div>

            {/* Left text */}
            <div className="card-left-text gsap-reveal order-3 lg:order-1 flex flex-col justify-center text-center lg:text-left z-20 w-full px-4 lg:px-0 lg:pr-8">
              <span className="hidden lg:block h-px w-10 bg-primary mb-6" aria-hidden="true" />
              <h3 className="text-white text-2xl md:text-3xl lg:text-[2.5rem] font-bold mb-0 lg:mb-5 tracking-tight leading-[1.1]">
                {cardHeading}
              </h3>
              <p className="hidden md:block text-neutral-400 text-sm md:text-base lg:text-[17px] leading-relaxed mx-auto lg:mx-0 max-w-sm">
                {cardDescription}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CinematicHero;
